import {
  TealiumEventMediaGroup,
  MediaBitrate,
  MediaQuality,
  MediaType,
  MediaAudioType,
  MediaVideoType,
  IGetEventGroupProps,
  tealiumEventMediaGroupPrefix,
  TealiumEventMediaGroupCC,
  ActivePodcastOrStation,
  StreamType,
} from '@4tn/core-audio-player-shared';

import { convertUtcToUnix } from '../utils/utility-functions';

export class TealiumEventMedia implements IGetEventGroupProps {
  private media_bitrate: MediaBitrate = MediaBitrate['64KBPS'];

  private media_datepublished: number | null = null;

  private media_duration = 0;

  private media_genre: string | null = null;

  private media_id = '';

  private media_name: string | null = null;

  private media_offset?: number | null = null;

  private media_program_episodenumber: number | null = null;

  private media_program_name: string | null = null;

  private media_program_season: number | null = null;

  private media_quality: MediaQuality | null = null;

  private media_type: MediaType = MediaType.AUDIO;

  private media_audiotype: MediaAudioType | null = null;

  private media_meta_description: string | null = null;

  private media_stationname: string | null = null;

  private media_thumbnail: string | null = null;

  private media_size: number | null = null;

  private media_videotype: MediaVideoType | null = null;

  constructor({
    mediaBitrate,
    mediaDatepublished,
    mediaDuration,
    mediaGenre,
    mediaId,
    mediaName,
    mediaOffset,
    mediaProgramEpisodenumber,
    mediaProgramName,
    mediaProgramSeason,
    mediaQuality,
    mediaType,
    mediaAudiotype,
    mediaMetaDescription,
    mediaStationname,
    mediaThumbnail,
    mediaSize,
    mediaVideotype,
  }: TealiumEventMediaGroupCC) {
    if (mediaBitrate) {
      this.media_bitrate = mediaBitrate;
    }
    if (mediaDatepublished) {
      this.media_datepublished = mediaDatepublished;
    }
    if (mediaDuration) {
      this.media_duration = mediaDuration;
    }
    if (mediaGenre) {
      this.media_genre = mediaGenre;
    }
    if (mediaId) {
      this.media_id = mediaId;
    }
    if (mediaName) {
      this.media_name = mediaName;
    }
    if (mediaOffset) {
      this.media_offset = Math.floor(mediaOffset);
    }
    if (mediaProgramEpisodenumber) {
      this.media_program_episodenumber = mediaProgramEpisodenumber;
    }
    if (mediaQuality) {
      this.media_quality = mediaQuality;
    }
    if (mediaProgramSeason) {
      this.media_program_season = mediaProgramSeason;
    }
    if (mediaProgramName) {
      this.media_program_name = mediaProgramName;
    }
    if (mediaType) {
      this.media_type = mediaType;
    }
    if (mediaAudiotype) {
      this.media_audiotype = mediaAudiotype;
    }
    if (mediaMetaDescription) {
      this.media_meta_description = mediaMetaDescription;
    }
    if (mediaStationname) {
      this.media_stationname = mediaStationname;
    }
    if (mediaThumbnail) {
      this.media_thumbnail = mediaThumbnail;
    }
    if (mediaSize) {
      this.media_size = mediaSize;
    }
    if (mediaVideotype) {
      this.media_videotype = mediaVideotype;
    }
  }

  public static parseMediaPublished(mediaDatepublished: string) {
    return convertUtcToUnix(mediaDatepublished);
  }

  public getEventGroupProps(): TealiumEventMediaGroup {
    const groupKeys = Object.keys(this).filter((k) => k.startsWith(tealiumEventMediaGroupPrefix));
    const eventGroupProps = groupKeys.reduce((o, k) => Object.assign(o, { [k]: this[k] }), {});
    return eventGroupProps as TealiumEventMediaGroup;
  }

  /**
   * the parseMediaMetadata function sets values for the list of props:
   * media_id,
   * media_name,
   * media_program_episodenumber,
   * media_program_name
   * media_program_season,
   * media_audiotype,
   * media_meta_discription,
   * media_stationname,
   * media_thumbnail
   * */
  public parseMediaMetadata(mediaMetadata: ActivePodcastOrStation) {
    const {
      publishDate,
      durationSeconds,
      id,
      title,
      podcastTitle,
      episode,
      contentTypeId,
      season,
      description,
      slug,
      images,
    } = mediaMetadata;
    if (id) {
      this.media_id = id;
    }
    if (podcastTitle || title) {
      this.media_name = podcastTitle || title;
    }
    if (publishDate) {
      this.media_datepublished = TealiumEventMedia.parseMediaPublished(publishDate);
    }
    this.media_duration = durationSeconds || 0;

    if (episode) {
      this.media_program_episodenumber = episode;
    }
    if (contentTypeId && contentTypeId === StreamType.PODCAST) {
      this.media_program_name = title || null;
    }
    if (season) {
      this.media_program_season = season;
    }
    this.media_audiotype = contentTypeId === StreamType.STATION ? MediaAudioType.RADIO_LIVE : MediaAudioType.PODCAST;
    if (description) {
      this.media_meta_description = description;
    }
    if (slug) {
      this.media_stationname = slug;
    }
    if (images) {
      this.media_thumbnail = images[0]?.uri || null;
    }
  }

  /**
   * The method validates media_offset prop with contentTypeId from mediaMetadata object
   * This is generic solution that makes sure that media offset is set only for
   * PODCAST and ON_DEMAND_CLIP content types
   * (The value is reset for STATION content types)
   */
  public validateMediaOffset(mediaMetadata: ActivePodcastOrStation) {
    if (mediaMetadata.contentTypeId === StreamType.STATION) {
      this.media_offset = null;
    }
  }
}
